import { HttpContext, type HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  output,
  signal,
  SimpleChanges,
  untracked,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '@environment';
import { Account } from '@frontendlib/common/types/account';
import { AccountService } from '@frontendlib/common/services/account.service';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { warmUpObservable } from '@frontendlib/common/decorators/warm-up-observable';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import type { DropdownSelectItem } from '@frontendlib/common/types/dropdown-select-item';
import { ComposeAttachmentsService, type PendingAttachment, type SeededUploadMode } from './compose-attachments.service';
import { SignaturesService } from '@modules/account/services/signatures.service';
import { TemplatesService } from '@modules/account/services/templates.service';
import { MessageTemplate } from '@modules/account/types/message-template';
import { Signature } from '@modules/account/types/signature';
import { AttachmentsService } from '@modules/attachments/services/attachments.service';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { normalizeEmailQuotes } from '@modules/common/utils/email-quote-styles';
import { asSignatureBlock, replaceOwnSignature } from '../../utils/signature-block';
import { insertDefaultSignature, pickDefaultSignature } from './default-signature';
import { EmailContentSanitizerService } from '@modules/common/services/email-content-sanitizer.service';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { UploadsService } from '@modules/common/services/uploads.service';
import { Application } from '@modules/common/types/application';
import { Upload } from '@modules/common/types/upload';
import { ContactsService } from '@modules/contacts/services/contacts.service';
import { Contact } from '@modules/contacts/types/contact';
import { DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { ComposeAttachmentItem } from '@modules/elements/components/compose-attachments/compose-attachments.component';
import { LinkedInfoData } from '@modules/linked-info/types/linked-info-data';
import { EnsureFwdPrefixPipe } from '@modules/messages/pipes/ensure-fwd-prefix.pipe';
import { IGNORE_SPACE_CONTEXT, OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import { MailboxTenantScopeService } from '@modules/common/services/mailbox-tenant-scope.service';
import { SendAsIdentitiesService, type SendAsIdentity } from '../../services/send-as-identities.service';
import { EnsureRePrefixPipe } from '@modules/messages/pipes/ensure-re-prefix.pipe';
import { FormatParticipantPipe } from '@modules/messages/pipes/format-participant.pipe';
import {
  type MailComposeOrigin,
  type MailWriteFeedback,
  type NativeDraftAcknowledgement,
  MessagesService,
  sendFailureReason,
} from '@modules/messages/services/messages.service';
import {
  type PendingSend,
  SEND_UNSAVED_MESSAGE,
  SendConfirmationService,
  type SendRecovery,
} from '@modules/messages/services/send-confirmation.service';
import { MailAppSettingsService } from '@modules/messages/services/settings.service';
import { Message, MessageFormGroupConfig, NO_SUBJECT_NAME } from '@modules/messages/types/message';
import { mailTypeOf, type MessageActionType } from '@modules/messages/types/message-action-type';
import { htmlToPlainText } from '@modules/common/utils/html';
import { ProductAnalyticsService } from '@frontendlib/common/services/product-analytics/product-analytics.service';
import { productAnalyticsTextCounts } from '@frontendlib/common/services/product-analytics/product-analytics-text';
import type {
  ProductAnalyticsClientEvents,
  ProductAnalyticsMailCounts,
  ProductAnalyticsMailType,
} from '@sharedlib/product-analytics-contract';
import { type Participant } from '@modules/messages/types/participant';
import { Settings } from '@modules/messages/types/settings';
import { applyInstanceDecorators } from '@sharedlib/common/lang/DeferInstanceDecorator';
import { canSendOutboundKnots } from '@sharedlib/common/utils/outbound-knots';
import { escapeHtml } from '@sharedlib/common/utils/escape-html';
import { isValidEmail } from '@sharedlib/common/utils/email';
import {
  ATTACHMENTS_WARNING_BYTES,
  isRecipientCapExceeded,
  RECIPIENT_CAP_MESSAGE,
} from '@sharedlib/common/types/mail-limits';
import {
  QUOTE_ATTRIBUTION_STYLE,
  QUOTE_BLOCKQUOTE_STYLE,
  QUOTE_DIVIDER_HTML,
} from '@sharedlib/common/utils/quote-html-styles';
import type { ParticipantSummaryDto } from '@sharedlib/dtos/dto/contacts-app/contact.dto';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  EMPTY,
  from,
  fromEvent,
  Observable,
  of,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  delayWhen,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

const SOURCE_ACCESS_UNCONFIRMED =
  'Source or mailbox access could not be confirmed. Your text is still here; no draft was created.';

/** Distinguishes an embedded image shown as a card (only oversized ones are) from a real attachment. */

/**
 * Escapes text destined for the quoted-forward body. The body is assembled as an HTML string, and a
 * subject or display name comes from a remote sender — `bodyHtml` is sanitized upstream, these are not.
 *
 * Accepts a missing value because the inputs only look non-nullable: a participant with no display name
 * falls back to its `address`, which reaches the frontend from a nullable column. A throw here escapes
 * `initByAction()` mid-seed and leaves the forward with a subject and an empty body, unretryable because
 * `actionSeeded` is already set.
 */

/**
 * This `bodyHtml` is sent verbatim as the outgoing email (see `eml.service.ts` on the backend, which
 * builds the EML directly from it with no CSS-inlining step) — most email clients strip `<style>`/
 * stylesheet rules, so the divider and quoted-thread formatting must be inlined here rather than left
 * to `rich-text-editor.component.less`'s `.mce-content-body hr`/`blockquote` rules, which only style the
 * in-app editor view. `QUOTE_DIVIDER_HTML`/`QUOTE_BLOCKQUOTE_STYLE` (from `@sharedlib/common`) are
 * shared with the MCP server's `message-tools.service.ts` so a Stitchy-sent reply/forward looks the
 * same as a user-sent one.
 */

@Component({
  selector: 'app-message-compose',
  templateUrl: './message-compose.component.html',
  styleUrls: ['./message-compose.component.less'],
  providers: [DateFormatPipe, ComposeAttachmentsService],
  standalone: false,
})
export class MessageComposeComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  /** Source for unique per-instance file-input ids (see `fileInputId`). */
  private static fileInputSeq = 0;

  // Inputs
  @Input() message: Message;
  @Input() linkedInfo: LinkedInfoData[];
  @Input() reply: Message;
  @Input() forward: Message[];
  @Input() action: MessageActionType;
  /** Compact layout (34px recipient/subject rows) — used by the compose modal. */
  @Input() dense = false;
  public readonly draftOrigin = input<MailComposeOrigin>();
  /** Files to attach once they have been made (SBUX-1179: a workbook's export); each shows a card meanwhile. */
  public readonly pendingAttachments = input<readonly PendingAttachment[]>([]);
  public readonly draftCreated = output<NativeDraftAcknowledgement>();
  protected readonly originStatus = signal('');
  protected readonly originPersistenceBlocked = signal(false);
  private originCreatePending = false;
  private originAcknowledged = false;
  private bindingGeneration = 0;
  protected readonly unavailableKnotsControl = new FormControl({ value: false, disabled: true }, { nonNullable: true });

  // Outputs
  @Output() close = new EventEmitter();
  /** Send was clicked and nothing on screen refuses it: the window leaves the screen while the send finishes. */
  public readonly hide = output<void>();
  /** "Reopen" on a send that left nothing in Drafts: the hidden window comes back as it was. */
  public readonly restore = output<void>();

  // Public
  public contactsSuggestions: AutocompleteFactory<ParticipantSummaryDto>;
  /** Space ends a recipient only after a full address, so a contact's name can be typed with spaces. */
  public readonly recipientCommitsOnSpace = isValidEmail;
  public form: FormGroup<MessageFormGroupConfig>;

  /**
   * Reply and Reply all pre-fill the body with a quoted thread below the cursor's intended starting point,
   * so the editor lands the cursor there instead of making the user click in. A forward has no recipient
   * yet, so its cursor goes to To (the compose dialog puts it there; Johanna, 2026-10-21), and a
   * freshly-resumed draft (no action) must not steal focus from the To field this way.
   */
  protected get focusBodyOnInit(): boolean {
    return !!this.action && this.action !== 'forward' && this.action !== 'forwardAsAttachment';
  }

  /** Container styles forwarded to the recipient inputs (To/CC/BCC). */
  public get recipientContainerStyles(): Record<string, string> {
    // `min-height` (not a fixed `height`) so the field keeps its compact 34px
    // when empty but grows as recipient chips wrap onto new lines instead of
    // clipping them. See the recipient overflow fix.
    return this.dense ? { 'min-height': '34px', padding: '0 8px 0 0' } : { padding: '8px 8px 8px 0' };
  }

  public cc = true;
  public bcc = false;
  /** A save (create/update) round trip is in flight. */
  protected readonly loading = signal(false);
  /**
   * A send round trip is in flight. Distinct from {@link loading} because a send is save+send and
   * the send part can fail on its own — notably when the backend refuses the message as spam,
   * which leaves the compose open with the draft intact.
   */
  protected readonly sending = signal(false);
  /**
   * The window is off the screen for a send, from the click until it is closed or "Reopen" brings it back. Its mailbox
   * is the one the send's requests carry, so nothing may re-seed it meanwhile.
   */
  private offScreenForSend = false;
  /** True while either leg of save-then-send is in flight; drives the footer buttons. */
  protected readonly busy = computed(() => this.loading() || this.sending());
  public showToolbar = false;
  public dragOver = 0;
  public droppedFiles = new ReplaySubject<FileList>();
  /**
   * Reveals the "drag a file from Stitch.ai here" drop zone in the compose body.
   * Toggled on via the attachment menu's "Insert file from Stitch.ai" option.
   */
  protected readonly showStitchFilesDropZone = signal(false);
  /**
   * Unique id linking the hidden file input to its `<label>` trigger in the attach
   * menu. A native label keeps the user activation needed to open the file dialog —
   * a JS `.click()` from inside the popover overlay loses it (browser blocks it).
   */
  protected readonly fileInputId = `compose-file-input-${MessageComposeComponent.fileInputSeq++}`;
  /**
   * Per-instance id for the inline editor's fixed toolbar container. Derived from
   * the unique `fileInputId` so multiple compose modals don't share one id — a
   * collision would misroute HugeRTE's `fixed_toolbar_container` to another
   * instance (and produce invalid duplicate-id HTML).
   */
  protected readonly toolbarContainerId = this.fileInputId.replace('file-input', 'format-toolbar');
  public customPopoverClose = new Subject<void>();
  public signatures: Signature[];
  public templates: MessageTemplate[];
  public editorCommandExecutor = new Subject<{ commandName: string; ui: boolean; content: string }>();

  // Outbound Knots Support
  public outboundKnotsMessageId: string = ''; // for outbound knots
  public canShowOutboundKnots: Observable<boolean>;
  /**
   * Most recently loaded mail settings. Cached so we can (a) seed the toggle
   * synchronously from `outboundKnotsEnabled` and (b) PUT back the full settings
   * (other fields preserved) when the user flips the toggle.
   */
  private mailSettings = new Settings();
  /**
   * Cached per-account "outbound knots" preference, migrated off localStorage to a
   * backend mail setting so it is shared across devices and visible to the Stitchy
   * MCP server. Defaults to `true` until the settings load resolves.
   */
  private get outboundKnotsEnabled(): boolean {
    return this.mailSettings.outboundKnotsEnabled ?? true;
  }
  /**
   * Emits `true` once the per-account mail settings have resolved. The outbound-knots
   * toggle is kept hidden (and its value off) until then, so it can only ever appear
   * seeded from the real preference — never from the pre-load default.
   */
  private readonly outboundKnotsSettingsLoaded$ = new BehaviorSubject<boolean>(false);
  // Private
  private readonly alive = new Subject<void>();
  // ReplaySubject(1) so late subscribers (e.g. canShowOutboundKnots via template's async pipe)
  // still receive the most recent trigger after .next() has already fired during ngOnInit.
  private readonly messageChanged = new ReplaySubject<void>(1);
  public formRegistrationKey: string | null = null;
  private editorSettled = false;
  /**
   * Seeding the form from `action` is a one-shot per compose session. `initByAction` runs from the
   * `getAccount()` subscription, and that is a BehaviorSubject any `getAccount(true)` elsewhere in the app
   * re-emits — a collapsed compose stays subscribed for its whole lifetime, so without this the quote, the
   * attachments and the "could not be attached" toast would all come back over whatever the user has
   * written and attached since. Cleared by `reset()`, which builds a fresh form.
   *
   */
  private actionSeeded = false;
  /**
   * Whether this compose manages the signature block of its body: it inserted the default, or the person
   * picked one from the toolbar. The default needs two answers — the list and, for a reply or forward, the quote
   * it goes under — that arrive in either order, and a `Send as` change brings a list from the other mailbox.
   * The block is managed for the mailbox it was put in for ({@link signatureTenant}): a second arrival for the
   * same mailbox neither stacks a copy nor takes the person's pick back, while a move to another mailbox
   * replaces the body's own signature block (found by its markers, see `signature-block.ts`), never text the
   * person wrote. Cleared by `reset()`.
   */
  private signatureManaged = false;
  /** The mailbox ({@link composeTenantId}) the managed signature block belongs to. */
  private signatureTenant: string | null = null;
  /**
   * Ticks whenever the mailbox this compose writes from changes (it is the one place {@link applySendAsIdentity}
   * commits), so the signature list is read again under the new one. A BehaviorSubject: `reset()` commits the
   * first identity in the constructor, before `ngOnInit` subscribes.
   */
  private readonly composeMailboxChanged = new BehaviorSubject<void>(undefined);
  /**
   * Whether a Reply All seed still has the user's own address among its recipients: the seeding ran against
   * the placeholder `new Account({})` that `AccountService` pushes when `GET /api/account` fails, so it had no
   * address to leave out. Finished by `excludeSelfFromRecipients()` when the real account arrives.
   */
  private replyAllSelfPending = false;
  private account: Account;
  private readonly ensureRePrefixPipe = inject(EnsureRePrefixPipe);
  private readonly ensureFwdPrefixPipe = inject(EnsureFwdPrefixPipe);
  private createdOrUpdatedMessage: Message;
  private readonly emailSanitizer = inject(EmailContentSanitizerService);

  private readonly productAnalytics = inject(ProductAnalyticsService);
  /** When this compose opened, for the time it took to send. */
  private readonly openedAt = Date.now();

  // Injected services
  private readonly messagesService = inject(MessagesService);
  private readonly contactsService = inject(ContactsService);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly attachmentsService = inject(AttachmentsService);
  private readonly uploadsService = inject(UploadsService);
  private readonly formatParticipantPipe = inject(FormatParticipantPipe);
  private readonly dateFormatPipe = inject(DateFormatPipe);
  private readonly accountService = inject(AccountService);
  private readonly signaturesService = inject(SignaturesService);
  private readonly templatesService = inject(TemplatesService);
  private readonly dynamicPanelService = inject(DynamicPanelService);
  private readonly formChangesModalsService = inject(FormChangesModalsService);
  private readonly toasterService = inject(ToasterService);
  private readonly sendConfirmation = inject(SendConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mailSettingsService = inject(MailAppSettingsService);
  private readonly sendAsIdentitiesService = inject(SendAsIdentitiesService);
  private readonly mailboxTenantScopeService = inject(MailboxTenantScopeService);
  /** Everything to do with attachments and uploads — see {@link ComposeAttachmentsService}. */
  protected readonly attachments = inject(ComposeAttachmentsService);

  // View Children
  @ViewChild('droppableArea') droppableArea: ElementRef;
  @ViewChild('fileAttachInput') fileAttachInput: ElementRef<HTMLInputElement>;

  // Callable attributes
  public readonly useInputTextGetValue: (text: string) => DropdownSelectItem<ParticipantSummaryDto>;

  // Callable attributes
  public dndPredicate =
    (currentItems: unknown[]) =>
    (dragData: DragData): boolean =>
      dragData.type === 'contact' &&
      dragData.data.every((item) => item?.['value']?.address || item?.['emails']?.length) &&
      !dragData.data.some((dragDataItem) =>
        currentItems.some(
          (item) => item['address'] === (dragDataItem['value']?.address || dragDataItem['emails'][0].value),
        ),
      );

  /** Only files dragged from the Files panel may land on the Stitch.ai drop zone. */
  public readonly stitchFileDropPredicate = (dragData: DragData): boolean => dragData.type === DragDataTypes.file;

  /**
   * Read-only projection of form.controls.scheduledAt value as a signal.
   * Do not set directly - update via form.controls.scheduledAt.setValue() instead.
   * The signal is synchronized with form control changes in ngOnInit.
   */
  protected readonly scheduledAtValue = signal<Date | null>(null);
  protected readonly hasRecipients = signal(false);
  /** True when distinct TO+CC+BCC recipients exceed the per-email cap — blocks Send (not Save draft). */
  protected readonly overRecipientCap = signal(false);
  protected readonly recipientCapMessage = RECIPIENT_CAP_MESSAGE;
  public readonly sendButtonText = computed(() => (this.scheduledAtValue() ? 'Schedule' : 'Send'));
  public readonly showSaveButton = computed(() => !this.scheduledAtValue());

  /**
   * The attachment surface the template binds. Thin pass-throughs rather than a second copy of the state:
   * the cards, the in-flight flag and the size-limit message are all owned by
   * {@link ComposeAttachmentsService}, and these exist because the template — and `app-compose-attachments`
   * through it — is the component's own API.
   */
  public get composeAttachments(): ComposeAttachmentItem[] {
    return this.attachments.items;
  }

  public set composeAttachments(items: ComposeAttachmentItem[]) {
    this.attachments.items = items;
  }

  public get filesUploading(): boolean {
    return this.attachments.uploading;
  }

  protected get validationMessages(): ComposeAttachmentsService['validationMessages'] {
    return this.attachments.validationMessages;
  }

  protected readonly attachmentsWarningLimit = ATTACHMENTS_WARNING_BYTES;

  /** Bound by `app-compose-attachments`'s `(remove)`. */
  protected removeAttachment(item: ComposeAttachmentItem): void {
    this.attachments.removeAttachment(item);
  }

  /**
   * Constructor
   */

  constructor() {
    applyInstanceDecorators(this, MessageComposeComponent);
    this.contactsSuggestions = this.contactsService.getEmailAutocompleteSuggestions(() => {
      const owner = this.message?.accountId ?? this.reply?.accountId ?? this.forward?.[0]?.accountId;
      if (owner) return { tenantItems: [{ accountId: owner }] };
      if (!this.bindingOffered || (this.composeTenantId() !== null && !this.sendAsSelected())) return undefined;
      return { requestContext: this.composeRequestContext() };
    });
    this.attachments.useRequestContext(this.uploadRequestContext);
    this.reset();
    // `maxFileSize` resolves asynchronously, so a compose opened during app startup would otherwise keep
    // the environment default for its lifetime and reject a file the account is entitled to.
    effect(() => {
      const limit = this.attachments.maxFileSize();
      untracked(() => this.attachments.scheduleFormMaxFileSize(limit));
    });

    this.useInputTextGetValue = (text: string) => {
      const participant: ParticipantSummaryDto = {
        address: text,
        contactId: '',
        id: '',
        name: '',
        addressCategory: '',
      };
      return {
        value: participant,
        source: participant,
        title: this.formatParticipantPipe.transform(participant),
      };
    };
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    // The file is made at once, but it uploads only once the mailbox is settled: see `mailboxSettled`.
    this.pendingAttachments().forEach((pending) =>
      this.attachments.attachPending({ ...pending, file: () => pending.file().pipe(delayWhen(() => this.mailboxSettled)) }),
    );
    // Two identities inside a team, none in the personal workspace. The default is the mailbox the
    // compose was opened from, which is what the active mailbox tenant already says (#961).
    this.sendAsIdentitiesService
      .identities()
      .pipe(takeUntil(this.alive))
      .subscribe({
        next: (identities) => {
          this.sendAsIdentities.set(identities);
          this.seedSendAsIdentity();
          this.identitiesArrived = true;
          this.mailboxNamed.next();
          this.changeDetectorRef.markForCheck();
        },
        // No list will come to name it, so the compose keeps the mailbox it opened on, and nothing can move it.
        error: () => this.mailboxNamed.next(),
      });

    this.accountService
      .getAccount()
      .pipe(takeUntil(this.alive))
      .subscribe((account) => {
        this.account = account;
        this.initByAction();
        this.applyDefaultSignature();
        this.excludeSelfFromRecipients();
      });

    // A signature belongs to a mailbox's space, so the list is read under the mailbox this compose writes from
    // (see `composeRequestContext`) and again whenever `Send as` moves it — the signature in the body and the
    // `From` on the wire must name the same mailbox.
    this.composeMailboxChanged
      .pipe(
        switchMap(() => {
          let requestContext: HttpContext;
          try {
            requestContext = this.composeRequestContext();
          } catch {
            // A source draft whose owning mailbox is not settled yet: the identity commit asks again.
            return EMPTY;
          }
          return this.signaturesService.search(undefined, { requestContext });
        }),
        takeUntil(this.alive),
      )
      .subscribe(({ items: signatures }) => {
        this.signatures = signatures;
        this.applyDefaultSignature();
      });

    this.templatesService
      .search()
      .pipe(takeUntil(this.alive))
      .subscribe(({ items: templates }) => (this.templates = templates));

    // Load the per-account "outbound knots" preference from the backend mail setting.
    // Cache it so the gate (canShowOutboundKnots) and reset() can seed the toggle synchronously.
    this.mailSettingsService
      .loadOnce()
      .pipe(takeUntil(this.alive))
      .subscribe((settings) => {
        // Clone into a local copy: the toggle mutates this for re-seed coherence, and we must
        // not mutate the service's shared cached instance (a failed PUT would otherwise leave
        // the cache wrong for other compose dialogs).
        this.mailSettings = new Settings({ ...settings });
        // Reveal the toggle only now that the real preference is known. This re-fires the
        // visibility gate, which seeds the control from `outboundKnotsEnabled` — so the
        // toggle never appears (or sends) with a stale pre-load default.
        this.outboundKnotsSettingsLoaded$.next(true);
      });

    this.messageChanged
      .pipe(
        tap(() => {
          this.bcc = this.message?.bcc?.length > 0;
        }),
        filter(() => this.action !== 'forwardAsAttachment'),
        map(() => {
          if (this.action === 'forward' && this.forward?.length) {
            return this.forward;
          } else if ((this.action === 'reply' || this.action === 'replyAll') && this.reply) {
            return [this.reply];
          } else if (this.message?.id) {
            return [this.message];
          } else {
            return [];
          }
        }),
        switchMap((messages) =>
          combineLatest(
            // The list is a request about the BOUND message, so it carries the same stamp as the create,
            // the autosaves and the send — a reopened team draft asks the mailbox its row is in, not the
            // one on screen (#971).
            messages.map((message) =>
              this.attachmentsService.listAll(message, undefined, this.composeRequestContext()),
            ),
          ).pipe(
            // Skip the seed rather than seeding an empty set: `uploads` is a replacement set, so claiming
            // "no attachments" on a failed read would have the next save delete what is stored. Completing
            // the inner observable leaves the outer subscription alive, so the next message still seeds —
            // without this the error tears the whole chain down for the compose's remaining life.
            catchError(() => EMPTY),
          ),
        ),
        takeUntil(this.alive),
      )
      .subscribe((attachments) => {
        const uploads: Upload[] = attachments
          .flat()
          .filter((attachment) => (this.action !== 'reply' && this.action !== 'replyAll') || attachment.inline)
          .map(
            (attachment) =>
              new Upload({
                id: attachment.attachmentId,
                name: attachment.fileName,
                type: attachment.fileType,
                size: attachment.fileSize,
                temp: false,
                inline: attachment.inline,
                copy: this.action === 'forward' ? attachment.stitchItem.id : null,
                checksum: attachment.checksum,
              }),
          );
        // Replies retain only quoted inline images; forwards retain all originals.
        // Removable only when editing the draft itself; a forward seeds the source message's originals,
        // which stay read-only — until one of them is oversized, when the card hands back the remove button,
        // because otherwise nothing on screen can lift the `uploads` validator off Save and Send.
        const mode: SeededUploadMode = !this.action && !!this.message?.isDraft() ? 'editable' : 'preserved';
        this.attachments.seedExisting(uploads, mode);
      });

    // Track scheduledAt form control value changes as a signal.
    // switchMap ensures old form subscriptions are cancelled when the form is recreated (via reset() in ngOnChanges).
    this.messageChanged
      .pipe(
        switchMap(() =>
          this.form.controls.scheduledAt.valueChanges.pipe(startWith(this.form.controls.scheduledAt.value)),
        ),
        takeUntil(this.alive),
      )
      .subscribe((value) => this.scheduledAtValue.set(value));

    // Shared recipient stream — resubscribes when form is recreated (messageChanged).
    const recipients$ = this.messageChanged.pipe(
      switchMap(() =>
        combineLatest([
          this.form.controls.to.valueChanges.pipe(startWith(this.form.controls.to.value)),
          this.form.controls.cc.valueChanges.pipe(startWith(this.form.controls.cc.value)),
          this.form.controls.bcc.valueChanges.pipe(startWith(this.form.controls.bcc.value)),
        ]),
      ),
    );

    // Track whether TO recipients are present — used to disable Send without blocking Save.
    // CC/BCC are optional; only TO is mandatory for sending.
    recipients$.pipe(takeUntil(this.alive)).subscribe(([to, cc, bcc]) => {
      const hasToRecipient = Array.isArray(to) && to.length > 0;
      this.hasRecipients.set(hasToRecipient);

      // Distinct recipient count across TO+CC+BCC, deduped by raw address. Guards the per-email cap client-side.
      const addresses = [...(to ?? []), ...(cc ?? []), ...(bcc ?? [])].map((recipient) => recipient?.address);
      this.overRecipientCap.set(isRecipientCapExceeded(addresses));
    });

    this.messageChanged.next();

    // we show outbound knots control only when at least one of the participants
    // is not from the stitch domain.
    // there is no sense in sending outbound knots inside Email for stitch users
    this.canShowOutboundKnots = combineLatest([
      recipients$,
      this.form.controls.title.valueChanges.pipe(startWith(this.form.controls.title.value)),
      this.form.controls.bodyHtml.valueChanges.pipe(startWith(this.form.controls.bodyHtml.value)),
      this.outboundKnotsSettingsLoaded$,
    ]).pipe(
      // Hidden until the preference resolves; then shown when the draft is eligible
      // (external recipient + enough content).
      map(([, , , settingsLoaded]) => settingsLoaded && this.isOutboundKnotsEligible()),
      tap((canShowOutboundKnots) => {
        // Seed the toggle from the per-account preference when shown; otherwise keep it
        // off (also covers switching an external recipient to an internal one, which
        // would otherwise trigger unnecessary knots extraction).
        this.applyOutboundKnotsSeed(canShowOutboundKnots);
      }),
    );

    // Persist a GENUINE user toggle of sendWithKnots to the per-account backend mail setting.
    // Programmatic seeds (reset() and the gate's tap restore branch) use { emitEvent: false }
    // so they never reach here — only a genuine user toggle does. Persist immediately (no
    // debounce): a debounce here would be cancelled by takeUntil(alive) if the user sends or
    // closes the compose right after toggling, dropping the preference.
    this.form.controls.sendWithKnots.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.alive))
      .subscribe((value) => {
        // Keep the local copy coherent so re-seeds (recipients/title/body changes) reflect the
        // user's current choice rather than the loaded default.
        this.mailSettings.outboundKnotsEnabled = !!value;
        this.persistOutboundKnotsPreference(!!value);
      });

    // Register form for unsaved changes detection
    this.formRegistrationKey = this.buildFormKey();
    this.formChangesModalsService.registerForm(
      this.formRegistrationKey,
      this.form,
      () =>
        this.save({ emit: true, toast: false }).pipe(
          tap(() => this.trackDraftSaved()),
          map(() => true),
          defaultIfEmpty(false),
          catchError(() => of(false)),
        ),
      () => this.close.emit(),
    );
  }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(this.droppableArea.nativeElement, 'dragover')
        .pipe(takeUntil(this.alive))
        .subscribe((event: DragEvent) => event.preventDefault());
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('message' in changes || 'draftOrigin' in changes) {
      // Unregister old form if exists
      if (this.formRegistrationKey) {
        this.formChangesModalsService.unregisterForm(this.formRegistrationKey);
      }
      this.reset();
      this.messageChanged.next();
      // Re-register with potentially new key
      this.formRegistrationKey = this.buildFormKey();
      this.formChangesModalsService.registerForm(
        this.formRegistrationKey,
        this.form,
        () =>
          this.save({ emit: true, toast: false }).pipe(
            tap(() => this.trackDraftSaved()),
            map(() => true),
            defaultIfEmpty(false),
            catchError(() => of(false)),
          ),
        () => this.close.emit(),
      );
      // messageId for outbound knots
      this.outboundKnotsMessageId = this.message?.id || '';

      if (this.message?.id) {
        this.messagesService.clearExpiredFollowUpIfAny(this.message);
        this.messagesService.clearExpiredSnoozeIfAny(this.message);
      }
    }
  }

  ngOnDestroy() {
    if (this.formRegistrationKey) {
      this.formChangesModalsService.unregisterForm(this.formRegistrationKey);
    }
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Builds the form registration key.
   *
   * Modal instances each provide their own `FormChangesModalsService`, so two
   * compose modals for the same message register in isolated maps — no key
   * namespacing is required.
   */
  private buildFormKey(): string {
    return this.message?.id || 'new-message-compose';
  }

  /**
   * Actions
   */

  /**
   * Whether the current draft is eligible to carry outbound knots — an external
   * recipient plus enough content. Reads live form values through the shared
   * `canSendOutboundKnots` SSOT (the same rule the visibility gate uses).
   */
  private isOutboundKnotsEligible(): boolean {
    const recipientAddresses = [
      ...(this.form.controls.to.value ?? []),
      ...(this.form.controls.cc.value ?? []),
      ...(this.form.controls.bcc.value ?? []),
    ].map((participant) => participant?.address);
    return canSendOutboundKnots({
      recipientAddresses,
      internalMailDomain: environment.mailUrl,
      title: this.form.controls.title.value,
      bodyHtml: this.form.controls.bodyHtml.value,
    });
  }

  /**
   * Seeds the sendWithKnots control from the per-account preference, gated by
   * eligibility. `{ emitEvent: false }` so this programmatic seed is NOT persisted
   * back to the backend — only a genuine user toggle is (see valueChanges above).
   */
  private applyOutboundKnotsSeed(eligible: boolean): void {
    this.form.controls.sendWithKnots.setValue(eligible ? this.outboundKnotsEnabled : false, { emitEvent: false });
  }

  /**
   * Persists the per-account outbound-knots preference. Fire-and-forget and deliberately NOT
   * bound to `this.alive`, so the PUT still reaches the backend if the compose is closed/sent
   * immediately after the toggle (otherwise the preference would be lost and Stitchy/MCP-created
   * mail would keep using the stale default).
   */
  private persistOutboundKnotsPreference(enabled: boolean): void {
    const mailboxTenantId = this.composeTenantId();
    if (!this.mailSettingsService.canUpdateSettings(mailboxTenantId)) return;
    this.mailSettingsService.updateOutboundKnotsEnabled(enabled, mailboxTenantId).subscribe({ error: () => undefined });
  }

  private reset(): void {
    this.bindingGeneration++;
    this.originCreatePending = false;
    this.originAcknowledged = false;
    this.originPersistenceBlocked.set(false);
    this.originStatus.set('');
    // One number for the validator and for every "oversized" decision. A card judged small enough by a
    // limit the validator does not share would be locked read-only while the form stayed invalid — dead
    // Send with no way out. `resolveFormMaxFileSize()` keeps the pair moving together afterwards.
    const maxAttachmentSize = this.attachments.beginSession();
    this.form = (this.message || new Message()).asFormGroup({ maxAttachmentSize });
    this.form.controls.bodyHtml.setValue(normalizeEmailQuotes(this.form.controls.bodyHtml.value), { emitEvent: false });
    this.attachments.useForm(this.form);
    if (!this.message?.id) this.attachments.showCarriedUploads(this.form.controls.uploads.value ?? []);
    // Freeze this compose's mailbox for its whole life, per bound message: the active mailbox is read at
    // dispatch by the interceptor, so switching mailboxes with a compose open would otherwise send half a
    // message's requests to the other tenant.
    const identities = this.sendAsIdentities();
    this.applySendAsIdentity(
      this.draftOrigin()?.initialIdentity ??
        this.identityForNewMessage(identities) ??
        this.sendAsIdentitiesService.defaultFor(identities),
    );
    // A reply or forward answers a parent that lives in ONE tenant, and an existing draft already has
    // one; only a message that does not exist yet has an identity left to choose.
    this.messageInMailbox.set(!!(this.message?.id || this.reply || this.forward?.length));
    // This binding has been named from the context only if there was a list to name it from. There is
    // none for a compose bound at creation — `reset()` runs before `ngOnInit` subscribes to an uncached
    // GET — and the first list to arrive is then this binding's own naming, not a re-seed (#961 row 17b).
    this.bindingOffered = this.identitiesArrived;
    // A new binding brings its own uploads (`beginSession()` above dropped the last one's), and its own
    // inline images with them.
    this.inlineUploadsDispatched.set(0);
    this.form.controls.sendWithKnots.setValue(this.outboundKnotsEnabled, { emitEvent: false });
    this.editorSettled = false;
    // A fresh form for a different message, so the action seeds it again.
    this.actionSeeded = false;
    this.signatureManaged = false;
    this.replyAllSelfPending = false;
    // Belongs to the message just replaced; `send()` prefers it over `this.message`.
    this.createdOrUpdatedMessage = undefined;
    this.initByAction();
    this.applyDefaultSignature();
    if (this.draftOrigin() && !this.message?.id) {
      this.form.controls.title.markAsDirty();
      this.form.controls.bodyHtml.markAsDirty();
    }
  }

  protected onEditorReady(): void {
    if (this.editorSettled) {
      return;
    }
    this.editorSettled = true;

    // HugeRTE normalizes HTML on init, which marks bodyHtml dirty via ControlValueAccessor.
    // If only bodyHtml is dirty (no real user edits), reset the form to pristine.
    const onlyBodyHtmlDirty =
      this.form.controls.bodyHtml.dirty &&
      Object.entries(this.form.controls).every(([key, ctrl]) => key === 'bodyHtml' || !ctrl.dirty);

    if (onlyBodyHtmlDirty && !this.draftOrigin()) {
      this.form.markAsPristine();
    }
  }

  /**
   * The identities this compose may send as — two inside a team, none in the personal workspace,
   * where there is one identity and a control with one option would be a dead control.
   */
  protected readonly sendAsIdentities = signal<readonly SendAsIdentity[]>([]);
  /**
   * Whether the identity list has answered at least once. It is an uncached GET, so `[]` on
   * {@link sendAsIdentities} means "the personal workspace" only after this is true — before that it
   * means "not asked yet", and the two must not be read as one (#961 row 17b).
   */
  private identitiesArrived = false;
  /**
   * Emits once the first identity list has named this compose's mailbox, or once that list has failed and nothing
   * will. SBUX-1179: a file the compose attaches by itself (Email Workbook's export) can be ready before the list
   * answers, and the first naming is the one naming the upload lock does not hold back ({@link seedSendAsIdentity}):
   * uploaded before it, the temp object lands in one mailbox and the message in another, and `fromTemp` fails every
   * save. Such a file uploads only after this; a file the person picks comes after the list in practice.
   */
  private readonly mailboxNamed = new ReplaySubject<void>(1);
  /**
   * Mailbox moves waiting on the Files panel's unsaved-changes prompt ({@link rescopeInsertFilePanel}). Until the
   * prompt is answered the compose still writes from the old mailbox, and accepting moves it: a file uploaded in
   * between would stay behind in the mailbox the message left.
   */
  private readonly mailboxMovesInFlight = new BehaviorSubject(0);
  /** Emits once the mailbox is named and no move is waiting on a prompt: when a file the compose attaches by itself uploads. */
  private readonly mailboxSettled = combineLatest([this.mailboxNamed, this.mailboxMovesInFlight]).pipe(
    filter(([, moves]) => moves === 0),
    take(1),
  );
  /**
   * Whether the current binding has already been named from an identity list — set by `reset()` from
   * {@link identitiesArrived} and by the seed itself. See {@link seedSendAsIdentity}.
   */
  private bindingOffered = false;
  /**
   * The chosen identity — the ONE value the label and the stamp both come from.
   *
   * Written only by {@link applySendAsIdentity}, which moves {@link composeTenantId} with it. Nothing
   * else may set either: the whole defect class here is a screen and a `space_id` deciding the mailbox
   * separately, and two setters is how they get to disagree again.
   */
  protected readonly sendAsSelected = signal<SendAsIdentity | undefined>(undefined);
  /**
   * The mailbox this compose belongs to, for the whole life of the bound message.
   *
   * Every request the compose makes about that message reads this one value — the create, the autosaves,
   * the send or schedule, the attachment upload the create resolves — so the row, the `From` on the wire,
   * the Sent copy and the temp object cannot end up in three different mailboxes. `undefined` means no
   * mailbox override at all, which is the personal workspace.
   *
   * Frozen by {@link applySendAsIdentity} rather than read per request: the interceptor reads the
   * *active* mailbox at dispatch, so a mailbox switch (or a `Send as` click) between one request and the
   * next would otherwise split a single message across two tenants.
   *
   * It is {@link sendAsSelected}'s tenant wherever there is an identity to choose. Where there is no
   * identity — the personal workspace — it is the tenant the interceptor WOULD have resolved at
   * dispatch, taken once, at open (`effectiveTenantId()`).
   *
   * Never `undefined`, and that is the fix for #993. The raw stored selection was the fallback, and it
   * is `undefined` for any personal workspace that has never had a mailbox clicked in it
   * (`setDefaultVirtualFolder()` stores nothing) — so the compose sent no override at all, and the
   * interceptor fell through to the mailbox on screen AT DISPATCH, which is the team the user has since
   * switched into. Resolving it here makes "nothing stored" and "`null` stored" the same request on the
   * wire, from the moment the compose opens.
   */
  private readonly composeTenantId = signal<string | null>(null);
  /**
   * Why the identity is settled, or `undefined` while it is still a choice.
   *
   * `'message'` — the message exists in a mailbox: an existing draft, a reply or forward whose parent
   * lives in one tenant, or a new compose whose first save has landed (a knots preview counts, by
   * writing `form.controls.id`). Changing it then would mean moving the row between mailboxes, and a
   * `From` selector does not move mail.
   *
   * `'upload'` — a file of this compose is in object storage under this tenant, or on its way there
   * with the tenant already stamped on the request, and `fromTemp(<message tenant>, …)` looks for it in
   * the message's. Switching after that is the #961 round-4 `NoSuchKey`; re-homing it instead would be
   * a cross-tenant copy (#971), so the first upload settles the identity and the control says so. Both
   * halves engage at DISPATCH, never on the answer: the in-flight window is the window the switch has
   * to be refused in (#961 rows 14, 18b).
   */
  private readonly sendAsLock = computed<'message' | 'upload' | undefined>(() => {
    if (this.messageInMailbox()) {
      return 'message';
    }
    return this.attachments.holdsTempObject() || this.attachments.holdsCarriedFile() || this.inlineUploadsDispatched() > 0 ? 'upload' : undefined;
  });
  /**
   * Whether this compose's message exists in a mailbox — the `'message'` half of {@link sendAsLock},
   * which outranks the upload half: once the row is written, taking the file away does not make the
   * sender a choice again.
   */
  private readonly messageInMailbox = signal(false);
  /**
   * Inline images of this compose the editor has dispatched to storage — the part of the upload half
   * the attachment cards cannot see, since a pasted image has no card.
   *
   * Counted at DISPATCH, not on the `imageUploaded` answer, and never released. Both are deliberate:
   *
   * - at dispatch, because the request is already on its way with this compose's tenant stamped on it.
   *   Counting the answer instead left the whole in-flight window unlocked, and flipping `Send as`
   *   inside it put the object in one mailbox and the create in the other — a create that then fails
   *   (`fromTemp` has no catch) while the orphaned `Upload` stays in `form.controls.uploads` and the
   *   lock forbids switching back, so the draft can never be saved at all (#961 rows 14, 18b).
   * - never released, because deleting the `<img>` from the body leaves the temp object in the tenant
   *   it went to (the editor sweeps those on destroy, not on edit) and its `Upload` stays in the form.
   *
   * The cost is a lock that outlives a FAILED inline upload, which the attachment cards do not pay.
   * Taken knowingly: a failed inline upload never reaches `form.controls.uploads`, so that compose
   * still saves and sends — a spurious lock there is an unnecessary label, against a draft that cannot
   * be saved at any price. Releasing it properly needs the shared `rich-text-editor` to report failure,
   * which it does not (#971).
   */
  private readonly inlineUploadsDispatched = signal(0);
  /** True once the identity is settled and the control is a label rather than a choice. */
  protected readonly sendAsLocked = computed(
    () =>
      this.sendAsLock() !== undefined ||
      !!this.draftOrigin()?.fixedOwnerId ||
      (!!this.draftOrigin() && !this.sendAsIdentities().length),
  );
  /**
   * Whether `Send as` has anything true to say: two identities to choose between, and — once the
   * identity is settled — one of them actually naming this compose.
   *
   * A compose opened in the personal workspace and still open after entering a team is the case that
   * needs this. It is bound to a mailbox neither team identity names (the personal workspace offers no
   * identities at all, so there is nothing to select), and now that row 17 stops the list re-homing it,
   * the settled label would render as two empty lines under "Send as:". Nothing is honest there; a
   * label naming no one is not.
   */
  protected readonly sendAsVisible = computed(
    () =>
      !!this.draftOrigin() || (this.sendAsIdentities().length > 1 && (!this.sendAsLocked() || !!this.sendAsSelected())),
  );
  /** True when it was an upload that settled it — the one lock the user did not obviously ask for. */
  protected readonly sendAsLockedByUpload = computed(() => this.sendAsLock() === 'upload');

  protected selectSendAs(identity: SendAsIdentity): void {
    if (this.sendAsLocked() || (this.draftOrigin() && this.originCreatePending)) {
      return;
    }
    this.applySendAsIdentity(identity);
  }

  /**
   * Seeds the default identity from the list the space context offers — every binding's FIRST list,
   * and after that only while the compose is still free to choose.
   *
   * The list reloads on every space-context change, and `sendAsSelected` alone cannot tell a bound
   * compose from a fresh one: the personal workspace offers no identities, so `defaultFor([])` is
   * `undefined` and nothing is ever "chosen" there. Without a second test, entering a team with a
   * personal compose open re-homed it — the autosave then `PUT`s a draft that is in the personal
   * account with `?space_id=<team>` (404, "couldn't save") and an already-uploaded temp object is
   * orphaned (#961 row 17).
   *
   * The test is {@link bindingOffered}, not {@link sendAsLocked} alone. A compose bound at creation —
   * every reopened draft, reply and forward — is locked *before any identity list exists*: `reset()`
   * runs from the constructor and from `ngOnChanges`, both before `ngOnInit` subscribes, and the list
   * is an uncached GET. Reading the lock as "already named" therefore refused the only naming those
   * composes ever get, leaving `Send as` unrendered and the stamp off the identity (#961 row 17b).
   * A compose that was never bound has nothing to protect and still follows the context it is now in
   * (#961 row 13).
   */
  private seedSendAsIdentity(): void {
    if (this.draftOrigin() || this.offScreenForSend) return;
    const reseed = this.bindingOffered;
    // This list is now the one this binding was named against, whether or not it named anything: a
    // later list is a space-context change and must not move a compose that is already committed.
    this.bindingOffered = true;
    if (this.sendAsSelected() || (reseed && this.sendAsLocked())) {
      return;
    }
    const identities = this.sendAsIdentities();
    this.applySendAsIdentity(
      this.identityForNewMessage(identities) ?? this.sendAsIdentitiesService.defaultFor(identities),
    );
  }

  /**
   * The identity a NEW message that names a workspace opens on (#1951): a compose started from an item
   * (Compose email on a contact) writes from that item's workspace, whatever mailbox Mail has selected.
   * The workspace's own identity when the list names it; otherwise, inside a team, the member identity,
   * because a My Workspace item's workspace is the personal account and the person's own mailbox inside
   * the team is the only "me" a team compose offers. `undefined` — today's rule — for a message that
   * names no workspace, an existing draft, a reply or forward, and the personal workspace (no list).
   */
  private identityForNewMessage(identities: readonly SendAsIdentity[]): SendAsIdentity | undefined {
    const workspaceId = this.message?.accountId;
    if (!workspaceId || this.message.id || this.reply || this.forward?.length || identities.length === 0) {
      return undefined;
    }
    return (
      identities.find(({ tenantId }) => tenantId === workspaceId) ?? identities.find(({ kind }) => kind === 'member')
    );
  }

  /**
   * The single writer of this compose's identity: the label and the tenant every request carries move
   * together or not at all.
   */
  private applySendAsIdentity(identity: SendAsIdentity | undefined): void {
    const tenantId = identity ? identity.tenantId : this.mailboxTenantScopeService.effectiveTenantId();
    const commit = (): void => {
      // The list in hand belongs to the mailbox just left; until the new one answers there is none, so a late
      // account answer cannot take the old mailbox's default for the new mailbox's.
      if (tenantId !== this.composeTenantId()) {
        this.signatures = undefined;
      }
      this.sendAsSelected.set(identity);
      this.composeTenantId.set(tenantId);
      this.composeMailboxChanged.next();
    };
    if (tenantId === this.composeTenantId()) {
      commit();
      return;
    }
    this.rescopeInsertFilePanel(tenantId, commit);
  }

  /**
   * The Files panel this compose opened through "Insert file from Stitch.ai" follows the identity it now
   * writes from (#1989): back to its root, listing that identity's workspace — through the same opening
   * Insert uses. A panel showing another app, or one the compose never opened, is not this compose's.
   */
  private rescopeInsertFilePanel(tenantId: string | null, commit: () => void): void {
    if (!this.showStitchFilesDropZone()) {
      commit();
      return;
    }
    let app: Application | null | undefined;
    this.dynamicPanelService
      .getApp()
      .pipe(take(1))
      .subscribe((current) => (app = current));
    if (app === Application.files) {
      let settled = false;
      const settle = (): void => {
        if (settled) return;
        settled = true;
        this.mailboxMovesInFlight.next(this.mailboxMovesInFlight.value - 1);
      };
      this.mailboxMovesInFlight.next(this.mailboxMovesInFlight.value + 1);
      this.dynamicPanelService.openApp(
        Application.files,
        tenantId,
        () => {
          commit();
          settle();
        },
        settle,
      );
      return;
    }
    commit();
  }

  /**
   * The stamp an ATTACHMENT upload carries, so it cannot go out under a tenant the create will not
   * look in.
   *
   * Reading it does NOT settle the identity. What settles it is a temp object existing under this
   * compose's tenant, which {@link sendAsLock} derives from the cards: an upload the server refuses
   * leaves nothing to be settled by, and latching here left "fixed by the uploaded file" on screen with
   * no file (#961 row 18). An attachment card is registered as `uploading` *before* its request is
   * dispatched, so the identity is still settled for the whole life of a real upload — earlier than the
   * latch managed.
   */
  protected readonly uploadRequestContext = (): HttpContext => this.composeRequestContext();

  /** Refuses an inline image that would take the email over its size limit, before the editor uploads it. */
  protected readonly inlineImageRefusal = (image: Blob): string | undefined =>
    this.attachments.inlineImageRefusal(image);

  /**
   * The same stamp for the editor's INLINE images, and the one place the compose learns that one has
   * been dispatched — a pasted image has no attachment card, so this read is the only account of it
   * the compose gets before the answer comes back. It therefore settles the identity here, where the
   * cards settle it at registration (#961 row 18b). See {@link inlineUploadsDispatched}.
   */
  protected readonly inlineUploadRequestContext = (): HttpContext => {
    this.inlineUploadsDispatched.update((count) => count + 1);
    this.changeDetectorRef.markForCheck();
    return this.composeRequestContext();
  };

  /**
   * The one stamp every request this compose makes carries: the create, the autosaves, the send or
   * schedule, the knots reprocess and the attachment upload the create has to resolve.
   *
   * The attachment *list* reads it too, since #971 moved `AttachmentsService` into the `[spaceContext]`
   * injector — module-provided it held the root `HttpClient`, whose requests never pass this interceptor
   * at all, so a reopened team draft listed none of its files. See the note on `AttachmentsService.listAll`.
   *
   * `OWNING_SPACE_ID` rather than `MAILBOX_TENANT_ID`, deliberately. On the wire the two are the same
   * `space_id`, but the mailbox token is honoured **only** for `/api/mail/…` (`spaces.interceptor.ts`),
   * and a message's tenant must not be lost by a request that happens to address another service: the
   * attachment upload goes to `/api/files/…`, so unstamped it lands in the space on screen while the row
   * lands in its own mailbox tenant, and mail-app's `fromTemp(<message tenant>, …)` then fails with
   * `NoSuchKey`. One token that no URL check can drop is the whole point.
   *
   * `null` — the signed-in user's own mailbox — has to **omit** `space_id` rather than stamp it, which is
   * what the mailbox token's `null` does too.
   *
   * There is no "no context" answer any more (#993): every compose names its mailbox, so a request can
   * never fall through to the mailbox on screen at dispatch. That fall-through is only harmless while
   * the two agree, and the whole defect is the window in which they do not.
   */
  private composeRequestContext(): HttpContext {
    const origin = this.draftOrigin();
    if (origin && (!this.sendAsSelected() || (origin.fixedOwnerId && origin.fixedOwnerId !== this.composeTenantId()))) {
      throw new Error('The source draft has no permitted owning mailbox');
    }
    const tenantId = this.composeTenantId();
    return tenantId === null
      ? new HttpContext().set(IGNORE_SPACE_CONTEXT, true)
      : new HttpContext().set(OWNING_SPACE_ID, tenantId);
  }

  @warmUpObservable
  save(feedbackConfig?: MailWriteFeedback, context?: HttpContext): Observable<Message> {
    if (!this.form.valid) {
      return EMPTY;
    }
    if (this.draftOrigin()) return this.saveOriginDraft(feedbackConfig, context);

    this.form.patchValue({ linkedInfo: this.linkedInfo }, { emitEvent: false });
    const message = Message.fromFormGroup(this.form);

    if (message.bodyHtml) {
      message.bodyHtml = this.emailSanitizer.restoreExternalImages(message.bodyHtml);
    }

    this.loading.set(true);
    // Update if message has an ID and is in editable state (DRAFT or SCHEDULED)
    const isEditable = message.isDraft() || message.isScheduled();
    // `Send as` rides on the TENANT stamp, not on a DTO field: the tenant already decides where the
    // row lands and where the Sent copy goes, so making the identity a function of it means the
    // `From` and the Sent-copy mailbox cannot disagree (#961). Both branches carry the SAME stamp —
    // an update addresses a row that already has a tenant, and stamping the one on screen instead
    // simply misses it.
    const requestContext = context ?? this.composeRequestContext();
    return (
      isEditable && message.id
        ? this.messagesService.update(message, feedbackConfig, requestContext)
        : this.messagesService.create(
            message,
            { emit: true, pending: feedbackConfig?.pending, recovery: feedbackConfig?.recovery },
            requestContext,
            (created) => this.recordCreatedDraft(created),
          )
    ).pipe(
      tap({
        next: (savedMessage) => this.handleSaveSuccess(savedMessage),
        error: () => {
          this.loading.set(false);
        },
      }),
    );
  }

  /** A source handoff never guesses whether a lost create response persisted a row. */
  private saveOriginDraft(feedbackConfig?: MailWriteFeedback, requestContext?: HttpContext): Observable<Message> {
    const origin = this.draftOrigin();
    if (!origin || this.originPersistenceBlocked() || this.originCreatePending) return EMPTY;
    const generation = this.bindingGeneration;
    const isNew = !this.form.controls.id.value;
    const chosenOwner = this.composeTenantId();
    let postDispatched = false;
    let submittedPayload = '';
    this.originCreatePending = true;
    this.loading.set(true);
    const authorization = isNew
      ? defer(() => (chosenOwner ? from(origin.beforeFirstCreate(chosenOwner)) : of(false)))
      : of(true);
    return authorization.pipe(
      switchMap((allowed) => {
        if (generation !== this.bindingGeneration) return EMPTY;
        if (!allowed || chosenOwner !== this.composeTenantId()) {
          this.originStatus.set('Source access could not be confirmed. Your text is still here; no draft was created.');
          this.originCreatePending = false;
          this.loading.set(false);
          return EMPTY;
        }
        return defer(() => {
          this.form.patchValue({ linkedInfo: this.linkedInfo }, { emitEvent: false });
          const message = Message.fromFormGroup(this.form);
          submittedPayload = this.payloadFingerprint();
          if (message.bodyHtml) message.bodyHtml = this.emailSanitizer.restoreExternalImages(message.bodyHtml);
          const context = requestContext ?? this.composeRequestContext();
          if (message.id) return this.messagesService.update(message, feedbackConfig, context);
          postDispatched = true;
          return this.messagesService.create(
            message,
            { emit: true, pending: feedbackConfig?.pending, recovery: feedbackConfig?.recovery },
            context,
            (saved) => this.acknowledgeOriginDraft(saved, generation),
          );
        });
      }),
      tap((saved) => {
        if (generation !== this.bindingGeneration) return;
        if (isNew) this.acknowledgeOriginDraft(saved, generation);
        const editedDuringSave = this.payloadFingerprint() !== submittedPayload;
        this.handleSaveSuccess(saved);
        if (editedDuringSave) this.form.markAsDirty();
        if (!this.originPersistenceBlocked())
          this.originStatus.set('Draft saved. Its mailbox is shown above; the source link does not grant access.');
        this.originCreatePending = false;
      }),
      catchError(() => {
        if (generation !== this.bindingGeneration) return EMPTY;
        this.originCreatePending = false;
        this.loading.set(false);
        if (postDispatched && !this.form.controls.id.value) {
          this.originPersistenceBlocked.set(true);
          this.originStatus.set(
            'Save outcome unknown. Keep this text and check native Drafts before creating another draft. Save is blocked to avoid a duplicate.',
          );
        } else if (this.form.controls.id.value) {
          this.originStatus.set(
            'The draft ID is known, but later processing failed. Your text is retained; Save updates that same draft.',
          );
        } else {
          this.originStatus.set(SOURCE_ACCESS_UNCONFIRMED);
        }
        return EMPTY;
      }),
      finalize(() => {
        if (generation !== this.bindingGeneration || !this.originCreatePending) return;
        this.originCreatePending = false;
        this.loading.set(false);
        if (postDispatched && !this.form.controls.id.value) {
          this.originPersistenceBlocked.set(true);
          this.originStatus.set(
            'Save outcome unknown. Keep this text and check native Drafts before creating another draft. Save is blocked to avoid a duplicate.',
          );
        }
      }),
    );
  }

  /**
   * The row a create wrote is this compose's draft from then on, even when the knowledge processing that follows the
   * write fails: a later save updates it rather than creating another, and a failed send opens it.
   */
  private recordCreatedDraft(message: Message): void {
    if (!message.id) return;
    this.createdOrUpdatedMessage = message;
    this.form.controls.id.setValue(message.id, { emitEvent: false });
    this.messageInMailbox.set(true);
  }

  /** Record the acknowledged ID before optional processing, caller callbacks or close. */
  private acknowledgeOriginDraft(message: Message, generation: number): void {
    if (generation !== this.bindingGeneration || this.originAcknowledged || !message.id || !message.accountId) return;
    this.originAcknowledged = true;
    this.createdOrUpdatedMessage = message;
    this.form.controls.id.setValue(message.id, { emitEvent: false });
    this.messageInMailbox.set(true);
    if (message.accountId !== this.composeTenantId()) {
      this.originPersistenceBlocked.set(true);
      this.originStatus.set(
        'The saved draft returned a different mailbox owner. Its ID is retained; further writes are blocked. Check native Drafts.',
      );
    }
    if (message.isDraft())
      this.draftCreated.emit({ id: message.id, accountId: message.accountId, itemType: message.itemType });
  }

  /** Clears a picked send-later time. Dirties the form: the server still holds the old value. */
  protected clearScheduledAt(): void {
    this.form.controls.scheduledAt.setValue(null);
    this.form.controls.scheduledAt.markAsDirty();
  }

  saveAndClose(): void {
    this.save()
      .pipe(takeUntil(this.alive))
      .subscribe({
        next: () => {
          this.trackDraftSaved();
          this.close.emit();
        },
        error: () => {},
      });
  }

  private trackDraftSaved(): void {
    this.productAnalytics.track('mail_draft_saved', { ...this.mailCounts(), type: this.mailType() });
  }

  /** Product analytics' type for this compose: its action, or what a reopened draft answers or passes on. */
  private mailType(): ProductAnalyticsMailType {
    return mailTypeOf(this.action, this.form.getRawValue());
  }

  /** This mail's shape for product analytics: counts only, the words being those above a quoted original. */
  private mailCounts(): ProductAnalyticsMailCounts {
    const { to, cc, bcc, uploads, bodyHtml } = this.form.controls;
    const attachments = (uploads.value ?? []).filter((upload) => !upload.inline);
    // The quoted original starts at its divider (QUOTE_DIVIDER_HTML, an `<hr>`); a new mail has none.
    const body = bodyHtml.value ?? '';
    const written = this.mailType() === 'new' ? body : body.split(/<hr\b/i)[0];
    const { words, chars } = productAnalyticsTextCounts(htmlToPlainText(written));
    return {
      to_count: to.value?.length ?? 0,
      cc_count: cc.value?.length ?? 0,
      bcc_count: bcc.value?.length ?? 0,
      attachment_count: attachments.length,
      attachment_size_kb: Math.round(attachments.reduce((total, upload) => total + (upload.size ?? 0), 0) / 1024),
      word_count: words,
      char_count: chars,
    };
  }

  /**
   * The form as the server would receive it, so a save can be compared against what the form holds now.
   *
   * Value-based rather than listening on `valueChanges`: a programmatic write that changes nothing emits
   * just like a keystroke. `uid` is masked because {@link handleSaveSuccess} writes it after the fact.
   */
  private payloadFingerprint(): string {
    // `uid` is masked because `handleSaveSuccess()` writes it after the payload went out, and
    // `sendWithKnots` because the mail-settings seed writes it from a gate that re-runs on every
    // edit — re-baselining there would swallow the edits this exists to catch.
    return JSON.stringify({
      ...Message.fromFormGroup(this.form).asPayloadJSON(),
      uid: null,
      sendWithKnots: null,
    });
  }

  private handleSaveSuccess(message: Message): void {
    this.loading.set(false);
    this.createdOrUpdatedMessage = message;
    this.outboundKnotsMessageId = message.id;
    // The message now lives in a mailbox, so the identity is no longer a choice — see {@link sendAsLock}.
    this.messageInMailbox.set(true);
    // `save()` picks create-vs-update off the form's own id, so without this every later save of a
    // compose that started empty creates another draft.
    this.form.controls.id.setValue(message.id, { emitEvent: false });
    // The form now matches the server, so a close.emit() from send/saveAndClose must not trip the
    // modal's unsaved-changes guard.
    this.form.markAsPristine();
  }

  /**
   * Saves the message and then sends it with the window off the screen, reporting the outcome by toast. Once the draft
   * exists the window closes whatever the answer, a refusal's toast opening the draft again; when nothing reached
   * Drafts it stays hidden, alive, until that toast's "Reopen" brings it back. The checks that need no request still
   * refuse with the window on screen.
   */
  protected send(): void {
    if (this.busy() || this.filesUploading || !this.form.valid) {
      return;
    }
    if (this.draftOrigin() && this.originPersistenceBlocked()) return;
    if (!this.hasRecipients()) {
      this.toasterService.show({ text: 'Recipients list required', icon: 'sapphire-close-small' });
      return;
    }
    if (this.overRecipientCap()) {
      this.toasterService.show({
        text: RECIPIENT_CAP_MESSAGE,
        icon: 'sapphire-close-small',
      });
      return;
    }
    // Read once, before anything leaves: the save and the send of this email carry the same mailbox whatever the
    // context does while the window is off the screen.
    let context: HttpContext;
    try {
      context = this.composeRequestContext();
    } catch {
      this.originStatus.set(SOURCE_ACCESS_UNCONFIRMED);
      return;
    }
    // Read as Send is pressed: the window leaves the screen at once, and the server answers later.
    const sentEvent: ProductAnalyticsClientEvents['mail_sent'] = {
      ...this.mailCounts(),
      type: this.mailType(),
      scheduled: !!this.form.controls.scheduledAt.value,
      with_knots: !!this.form.controls.sendWithKnots.value,
      compose_seconds: Math.round((Date.now() - this.openedAt) / 1000),
    };
    this.sending.set(true);
    const pending = this.sendConfirmation.begin();
    this.destroyRef.onDestroy(() => pending.release());
    this.offScreenForSend = true;
    this.hide.emit();
    // Always save first, so what goes out is what the compose is showing. Skipping the save on a
    // pristine form would deliver the last persisted copy instead — and `dirty` misses a seeded
    // `sendWithKnots`, a template or signature insertion, and anything typed while a save is in
    // flight. A re-save of an unchanged draft is a plain `PUT` the backend reconciles by checksum.
    const unsaved = this.reopenRecovery(pending);
    const draftBefore = this.createdOrUpdatedMessage;
    let saved: Message | undefined;
    let sent = false;
    const originGeneration = this.draftOrigin() ? this.bindingGeneration : null;
    // A create whose later processing failed still wrote the row, and an origin save swallows its error.
    const savedDraft = (): Message | undefined =>
      saved ?? (this.createdOrUpdatedMessage !== draftBefore ? this.createdOrUpdatedMessage : undefined);
    const sameBinding = (): boolean => originGeneration === null || originGeneration === this.bindingGeneration;

    // Not bound to `alive`: the window is off the screen, and its teardown must not cancel the send it started.
    this.save({ emit: true, toast: false, pending, recovery: unsaved }, context)
      .pipe(
        tap((message) => (saved = message)),
        switchMap((message) =>
          this.messagesService.send(
            message,
            { emit: true, pending, recovery: this.messagesService.draftRecovery(message) },
            context,
          ),
        ),
      )
      .subscribe({
        next: () => {
          sent = true;
          this.productAnalytics.track('mail_sent', sentEvent);
          this.sending.set(false);
          pending.settle();
          this.closeAfterSend();
        },
        error: (error: HttpErrorResponse) => {
          // The save before the send can be refused as well as the send (a mail too large, say).
          this.productAnalytics.track('mail_send_failed', { reason: sendFailureReason(error) });
          this.reportUnsent(pending, unsaved, savedDraft(), sameBinding());
        },
        complete: () => {
          if (!sent) this.reportUnsent(pending, unsaved, savedDraft(), sameBinding());
        },
      })
      .add(() => pending.settle());
  }

  /** "Reopen", for an email nothing of which reached Drafts. Closing its toast any other way reopens it too. */
  private reopenRecovery(pending: PendingSend): SendRecovery {
    const reopen = (): void => {
      pending.release();
      if (this.destroyRef.destroyed) return;
      this.offScreenForSend = false;
      this.restore.emit();
    };
    return { action: { text: 'Reopen', handler: reopen }, text: SEND_UNSAVED_MESSAGE, held: reopen };
  }

  /**
   * The send did not go through. A saved draft closes the window, its toast opening the draft. Anything else stays in
   * the hidden window until "Reopen"; its close guard must then fire, or a reply/forward — pristine until the user
   * types — closes silently. A binding replaced in the meantime keeps its own form state.
   */
  private reportUnsent(
    pending: PendingSend,
    unsaved: SendRecovery,
    draft: Message | undefined,
    sameBinding: boolean,
  ): void {
    this.sending.set(false);
    if (draft) {
      pending.fail(this.messagesService.draftRecovery(draft));
      this.closeAfterSend();
      return;
    }
    if (sameBinding) this.form.markAsDirty();
    pending.fail(unsaved);
  }

  /**
   * Destroys the hidden window once its send is settled. Nothing on it can be unsaved — it left the screen on the
   * click, with what the save carried — so the close guard must not ask about a window nobody can see.
   */
  private closeAfterSend(): void {
    this.form.markAsPristine();
    this.close.emit();
  }

  /**
   * Requests knots to be processed for the current message
   * Response will come a web-socket signal SignalStitchEventType.KNOTS_UPDATED
   * Somebody should listen for this signal and re-fetch the knots for the current message
   */
  public requestKnotPreview(): void {
    this.save({ emit: true, toast: false })
      .pipe(
        switchMap((message) =>
          this.messagesService.reprocessKnots([message], { requestContext: this.composeRequestContext() }),
        ),
        takeUntil(this.alive),
      )
      .subscribe();
  }

  selectTemplate(template: MessageTemplate) {
    Message.formatSignatureTemplate(this.form.controls.bodyHtml, template);
    this.customPopoverClose.next();
  }

  selectSignature(signature: Signature) {
    Message.formatSignatureTemplate(this.form.controls.bodyHtml, signature);
    // A signature picked from this mailbox's list is the block `Send as` has to move with the mailbox, in
    // place of the default it replaced; otherwise the other mailbox's signature would go out on this message.
    this.signatureManaged = true;
    this.signatureTenant = this.composeTenantId();
    this.customPopoverClose.next();
  }

  handleNewTemplate(type) {
    this.close.emit();
    this.router.navigate(['/settings', 'mail', type]);
  }

  handleDropFiles(event: DragEvent) {
    this.dragOver = 0;
    event.stopPropagation();
    event.preventDefault();

    this.droppedFiles.next(event.dataTransfer.files);
  }

  handleDrop(dragData: DragData, controlName: string) {
    const items = dragData.data.map((item) =>
      item instanceof Contact ? { name: item.title, address: item.emails?.[0]?.value } : item['value'],
    );

    this.form.controls[controlName].setValue(this.form.controls[controlName].value.concat(items));
    this.form.controls[controlName].markAsDirty();
  }

  handleEditorUploadedImage(upload: Upload) {
    this.attachments.addInlineImage(upload);
  }

  protected handleEditorImageUploadFailed(image: Blob): void {
    this.attachments.releaseInlineImage(image);
  }

  /** Opens HugeRTE's Insert Image dialog — same action as the editor's Insert > Image menu item. */
  protected insertImage(): void {
    this.editorCommandExecutor.next({ commandName: 'mceImage', ui: true, content: '' });
  }

  /** Opens a native file picker so the user can attach files to the message. */
  protected attachFile(): void {
    this.fileAttachInput?.nativeElement.click();
  }

  /**
   * "Insert file from Stitch.ai" menu action: opens the Files list in the dynamic
   * panel and reveals the drop zone in the compose body, so the user can drag a
   * file from the panel onto the message.
   */
  protected insertFileFromStitch(): void {
    this.showStitchFilesDropZone.set(true);
    this.dynamicPanelService.openApp(Application.files, this.composeTenantId());
  }

  /**
   * Handles files dragged from the Files panel onto the Stitch.ai drop zone.
   * Each dropped file is attached by reference (no re-upload) — see
   * {@link attachStitchFile}.
   */
  protected handleStitchFileDrop(dragData: DragData): void {
    if (dragData.type !== DragDataTypes.file) {
      return;
    }
    this.attachments.attachStitchFiles(dragData);
  }

  /**
   * Uploads files chosen via the paperclip's hidden input. For each file:
   *   - inserts a `ComposeAttachmentItem` with `status: 'uploading'` so the
   *     <app-compose-attachments> grid renders an in-flight card immediately;
   *   - subscribes to `upload.progress` and updates the item's progress (0–100);
   *   - on success flips the item to `status: 'done'` and appends the
   *     resulting Upload to `form.controls.uploads` (so it ships with the
   *     message on save/send);
   *   - on error flips the item to `status: 'failed'` so the grid shows the
   *     peach "Upload failed" card.
   *
   * The tracker array is replaced immutably (new array reference per update)
   * to satisfy the child component's OnPush change detection.
   */
  protected handleAttachFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) return;

    this.attachments.uploadFiles(files);

    // The attach menu has served its purpose once a file was added from the device —
    // close it so it doesn't linger over the compose.
    this.customPopoverClose.next();

    // Reset so picking the same file twice in a row still triggers (change).
    input.value = '';
  }

  /**
   * Computes recipients for Reply-to-All per Outlook semantics: To gets the
   * original sender plus original To recipients, Cc keeps the original Cc.
   * Self is excluded (case-insensitive) and addresses appearing in both lists
   * are placed in To only.
   */
  private computeReplyAllRecipients(reply: Message): { to: Participant[]; cc: Participant[] } {
    const selfAddress = this.account.email?.toLowerCase();
    const seen = new Set<string>();
    const filterAndDedupe = (participants: ReadonlyArray<Participant | undefined>): Participant[] => {
      const result: Participant[] = [];
      for (const participant of participants) {
        if (!participant?.address) continue;
        const key = participant.address.toLowerCase();
        if (key === selfAddress || seen.has(key)) continue;
        seen.add(key);
        result.push(participant);
      }
      return result;
    };

    return {
      to: filterAndDedupe([reply.from, ...(reply.to ?? [])]),
      cc: filterAndDedupe(reply.cc ?? []),
    };
  }

  /**
   * Takes the user's own address out of a Reply All that was seeded before the account was known.
   *
   * The recipients only. Re-running the seeding would re-assign the subject and the body with them and throw
   * away whatever the user has typed since — and a second emission is not a rarity here: the placeholder
   * account carries no id, so the next `getAccount()` anywhere in the app re-fetches and pushes again.
   */
  private excludeSelfFromRecipients(): void {
    if (!this.replyAllSelfPending || !this.account.email) {
      return;
    }
    this.replyAllSelfPending = false;

    const selfAddress = this.account.email.toLowerCase();
    const withoutSelf = (participants: Participant[]): Participant[] =>
      (participants || []).filter((participant) => participant.address?.toLowerCase() !== selfAddress);

    this.form.controls.to.setValue(withoutSelf(this.form.controls.to.value));
    this.form.controls.cc.setValue(withoutSelf(this.form.controls.cc.value));
  }

  /**
   * Opens a new message, reply or forward with the default signature, the way the mobile composer does, and
   * keeps that block in step with the mailbox the compose writes from. A reopened draft already holds whatever
   * signature it was written with, and a changed one would land as a second, different block in somebody's
   * half-written message, so it is left alone.
   * Programmatic like the action seed: it does not dirty the form, so an untouched compose still closes
   * without the unsaved-changes prompt.
   */
  private applyDefaultSignature(): void {
    if (!this.signatures) {
      return;
    }
    // A reply or forward waits for its quote, so the signature lands above it, not before it exists.
    if (this.action && !this.actionSeeded) {
      return;
    }
    if (this.message?.id) {
      return;
    }
    const tenant = this.composeTenantId();
    // A block already in the body for THIS mailbox stays — the default, or one the person picked.
    if (this.signatureManaged && this.signatureTenant === tenant) {
      return;
    }
    const wanted = pickDefaultSignature(this.signatures, !!this.action);
    const bodyHtml = this.form.controls.bodyHtml;
    const body: string = bodyHtml.value ?? '';
    if (!this.signatureManaged) {
      if (!wanted) {
        return;
      }
      this.signatureManaged = true;
      this.signatureTenant = tenant;
      const seeded = insertDefaultSignature(body, wanted);
      if (seeded !== body) {
        bodyHtml.setValue(seeded);
      }
      return;
    }
    // Moved to another mailbox: the body's own block becomes this mailbox's default, or goes if it has none. A
    // block the person already deleted stays deleted.
    this.signatureTenant = tenant;
    const block = asSignatureBlock(wanted?.content);
    this.signatureManaged = block !== null;
    const moved = replaceOwnSignature(body, block);
    if (moved !== body) {
      bodyHtml.setValue(moved);
    }
  }

  private initByAction(): void {
    if (!this.action || !this.account || this.actionSeeded) {
      return;
    }
    // Set before the seeding rather than after it: the guard is re-entrancy protection, and the
    // forwardAsAttachment branch starts async work it must not start twice. A throw below therefore leaves
    // the compose marked seeded — deliberate, since a re-entry would only repeat the same failure.
    this.actionSeeded = true;

    // https://support.microsoft.com/en-us/office/reply-to-or-forward-an-email-message-a843f8d3-01b0-48da-96f5-a71f70d0d7c8#:~:text=Reply%20all%20sends%20the%20new,when%20you%20forward%20a%20message.

    if ((this.action === 'forward' || this.action === 'forwardAsAttachment') && this.forward?.length) {
      const transformedTitle = this.ensureFwdPrefixPipe.transform(this.forward[0].title);
      this.form.controls.title.setValue(transformedTitle);
      this.form.controls.forwardOf.setValue(this.forward[0].id);

      if (this.action === 'forwardAsAttachment') {
        // Whatever has no stored `.eml` is quoted inline instead, the way a plain forward would carry it —
        // so every message the user picked travels one way or the other. An attached message is not also
        // quoted: it goes as a file, and the quote would duplicate it.
        const attachable: Message[] = [];
        const quotedInstead: Message[] = [];
        for (const message of this.forward) {
          (Message.canForwardAsAttachment(message) ? attachable : quotedInstead).push(message);
        }

        this.attachments.seedEmlUploads(attachable);

        if (quotedInstead.length > 0) {
          this.toasterService.show({
            text: `${quotedInstead.length} message${quotedInstead.length > 1 ? 's' : ''} could not be attached, so ${quotedInstead.length > 1 ? 'their' : 'its'} content was forwarded inline instead`,
            icon: 'sapphire-close-small',
          });
          this.form.controls.bodyHtml.setValue(this.buildForwardedBodyHtml(quotedInstead));
          // The size warning is left to this call. An original arriving here can force a generated `.eml` to
          // be renamed, and the warning has to name the file the card shows — warned about now, it could
          // name a filename that no longer exists by the time the user goes looking for it.
          this.attachments.appendQuotedMessageAttachments(quotedInstead);
        } else {
          this.attachments.warnAboutOversizedUploads(this.form.controls.uploads.value || []);
        }
        return;
      }

      this.form.controls.bodyHtml.setValue(this.buildForwardedBodyHtml(this.forward));
    }

    if ((this.action === 'reply' || this.action === 'replyAll') && this.reply) {
      const transformedTitle = this.ensureRePrefixPipe.transform(this.reply.title);
      this.form.controls.title.setValue(transformedTitle);
      this.form.controls.replyTo.setValue(this.reply.id);

      if (this.action === 'reply') {
        this.form.controls.to.setValue([this.reply.from].filter(Boolean));
      } else {
        // replyAll - here
        const { to, cc } = this.computeReplyAllRecipients(this.reply);
        this.form.controls.to.setValue(to);
        this.form.controls.cc.setValue(cc);
        // With no account there was no address to exclude — `excludeSelfFromRecipients()` finishes it.
        this.replyAllSelfPending = !this.account.email;
      }

      const replyBodyHtml = `
        <div style="margin:0;"><br/></div>
        ${QUOTE_DIVIDER_HTML}
        <p style="${QUOTE_ATTRIBUTION_STYLE}">On ${this.dateFormatPipe.transform(this.reply.date, 'dateTimeFull')} ${this.formatParticipant(this.reply.from)} wrote:</p>
        <blockquote style="${QUOTE_BLOCKQUOTE_STYLE}">${normalizeEmailQuotes(this.reply.bodyHtml) || '<br/>'}</blockquote>
      `;

      this.form.controls.bodyHtml.setValue(normalizeEmailQuotes(replyBodyHtml));
    }
  }

  /**
   * One participant as a quoted header — forward or reply — shows them, escaped for the HTML string it
   * lands in.
   * Escapes the pipe's plain-text form rather than its `html` form, which pre-encodes only the angle
   * brackets around the address and would double-encode here — the name and address themselves, both
   * remote-controlled, are what needs escaping.
   */
  private formatParticipant(participant: Participant): string {
    return escapeHtml(this.formatParticipantPipe.transform(participant));
  }

  /** The quoted "Forwarded message" block that a plain forward puts in the body. */
  private buildForwardedBodyHtml(messages: Message[]): string {
    return normalizeEmailQuotes(
      `<div style="margin:0;"><br/></div>${QUOTE_DIVIDER_HTML}` +
        messages
          .map(
            (message) => `
            <p style="${QUOTE_ATTRIBUTION_STYLE}">Forwarded message</p>
            <blockquote style="${QUOTE_BLOCKQUOTE_STYLE}">
            From: ${this.formatParticipant(message.from)}
            <br/>
            Date: ${this.dateFormatPipe.transform(message.date, 'dateTimeFull')}
            <br/>
            Subject: ${escapeHtml(message.title || NO_SUBJECT_NAME)}
            <br/>
            To: ${(message.to || []).map((participant) => this.formatParticipant(participant)).join(', ')}
            <br/>
            CC: ${(message.cc || []).map((participant) => this.formatParticipant(participant)).join(', ')}
            <br/>
            <br/>

            ${normalizeEmailQuotes(message.bodyHtml)}
            </blockquote>
          `,
          )
          .join('<br/><br/>'),
    );
  }
}
